import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Scale, FileText } from 'lucide-react';
import PageTitle from '../hooks/PageTitle';

const TermsOfEngagement = () => {
  const sections = [
    { id: '1', title: '1. Our Terms', href: '#our-terms' },
    { id: '2', title: '2. Our Charges', href: '#charges' },
    { id: '3', title: '3. Costs on Account', href: '#costs' },
    { id: '4', title: '4. Payments', href: '#payments' },
    { id: '5', title: '5. Liability', href: '#liability' },
    { id: '6', title: '6. Our Legal Advice', href: '#advice' },
    { id: '7', title: '7. Work Done by Others', href: '#others' },
    { id: '8', title: '8. Use of Information', href: '#information' },
    { id: '9', title: '9. Insurance and Regulation', href: '#insurance' },
    { id: '10', title: '10. Proof of Identity', href: '#identity' },
    { id: '11', title: '11. Complaints', href: '#complaints' },
    { id: '12', title: '12. Communications', href: '#communications' },
    { id: '13', title: '13. Termination', href: '#termination' },
    { id: '14', title: '14. Law and Claims', href: '#law' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <PageTitle title="Terms of Engagement" />
      {/* Header */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center text-sm text-gray-300 mb-4">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="h-4 w-4 mx-2" />
            <span>Terms of Engagement</span>
          </div>
          <div className="flex items-center space-x-4 mb-6">
            <div className="bg-white/10 p-3 rounded-lg">
              <Scale className="h-8 w-8" />
            </div>
            <h1 className="text-4xl md:text-5xl font-serif">Terms of Engagement</h1>
          </div>
          <p className="text-lg text-gray-300 max-w-3xl">
            These terms govern the provision of legal services by Ashbourne Law to our clients. Please read them carefully before engaging our services.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-gray-50 rounded-lg p-6">
              <h3 className="font-medium text-gray-900 mb-4">Sections</h3>
              <nav className="space-y-2">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={section.href}
                    className="block text-sm text-gray-600 hover:text-gray-900 transition-colors py-1"
                  >
                    {section.title}
                  </a>
                ))}
              </nav>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            <div className="prose prose-lg max-w-none">
              {/* Section 1 */}
              <section id="our-terms" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">1. Our Terms</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    <strong>1.1 Parties.</strong> In these terms, Ashbourne Law Limited is referred to as "<strong>Ashbourne/we/our/us</strong>". The clients of Ashbourne are referred to as "<strong>you/your</strong>". This includes those authorised to provide instructions on the client's behalf.
                  </p>
                  <p>
                    <strong>1.2 Our status.</strong> We are an English limited liability company and an international boutique law firm with offices in London and other jurisdictions.
                  </p>
                  <p>
                    <strong>1.3 Professional titles.</strong> We use professional titles to designate the seniority and role of our colleagues. Those we call 'Partners' are solicitors, barristers, and other legal professionals with extensive experience. The title 'Partner' is a professional designation only.
                  </p>
                  <p>
                    <strong>1.4 Entire terms.</strong> These terms (and any linked agreement) constitute the entire agreement between us. They will not change unless we replace them in their entirety with written notice.
                  </p>
                  <p>
                    <strong>1.5 Joint engagement.</strong> Where we act for more than one client, the engagement is a joint engagement and all clients are jointly and severally liable for our fees.
                  </p>
                </div>
              </section>

              {/* Section 2 */}
              <section id="charges" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">2. Our Charges</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    <strong>2.1 Fees.</strong> The way we charge for our services is set out in our Engagement Letter. We may offer a fixed or capped fee for certain matters. Where we do not offer a fixed fee, we charge for the time we spend working for you at our hourly rates. We record time spent to the nearest six-minute unit.
                  </p>
                  <p>
                    <strong>2.2 Estimates.</strong> Any estimates we provide are based on our understanding of the work to be undertaken. An estimate is not a cap and we may exceed our estimates if the matter becomes more complex than anticipated.
                  </p>
                  <p>
                    <strong>2.3 Disbursements.</strong> In connection with your matter, we may incur expenses ("disbursements") such as court fees, counsel fees, or search fees. These will be charged to you at cost plus any applicable VAT.
                  </p>
                  <p>
                    <strong>2.4 VAT.</strong> We quote prices exclusive of Value Added Tax ("VAT"). VAT is added to our invoices at the prevailing rate.
                  </p>
                  <p>
                    <strong>2.5 Rate reviews.</strong> We review our rates annually and will provide written notice of any increases with at least one month's notice.
                  </p>
                </div>
              </section>

              {/* Section 3 */}
              <section id="costs" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">3. Costs on Account</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    We may request a payment on account of costs, disbursements, and VAT before commencing work or at any time during the engagement. From time to time, we may request additional payments on account. At the conclusion of the matter, any unused funds will be returned to you promptly.
                  </p>
                  <p>
                    We are not obliged to commence or continue work if requested payments on account are not received. We may terminate the engagement if payment on account is not received within a reasonable timeframe.
                  </p>
                </div>
              </section>

              {/* Section 4 */}
              <section id="payments" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">4. Payments</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    <strong>4.1 Invoices.</strong> We may issue interim invoices during the course of a matter. Unless otherwise stated, all invoices are payable within 14 days of the invoice date.
                  </p>
                  <p>
                    <strong>4.2 Payment methods.</strong> You can pay us by bank transfer or credit/debit card. Our bank details are provided on each invoice.
                  </p>
                  <p>
                    <strong>4.3 Late payment.</strong> If you do not pay an invoice when due, we reserve the right to charge interest at the statutory rate and to suspend work on your matter until payment is received.
                  </p>
                  <p>
                    <strong>4.4 Currency.</strong> If you pay us in a currency other than that stated on the invoice, we will convert the payment at our bank's standard exchange rates and deduct any charges incurred.
                  </p>
                  <p>
                    <strong>4.5 Security.</strong> Before sending funds, please reconfirm our bank details by telephoning our office. We will never change our bank details via email. If you receive any communication suggesting our bank details have changed, please contact us immediately by telephone.
                  </p>
                </div>
              </section>

              {/* Section 5 */}
              <section id="liability" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">5. Liability</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    <strong>5.1 Our liability.</strong> We are liable for losses caused directly by our breach of duty or negligence, to the extent such losses are reasonably foreseeable. We are not liable for indirect or consequential losses, loss of profits, or damage to reputation.
                  </p>
                  <p>
                    <strong>5.2 Liability cap.</strong> Our total liability to you in connection with any single matter or group of related matters is limited to £5,000,000. This limit applies to all claims arising from the engagement. You may request an increase to this limit, which may result in an adjustment to our fees.
                  </p>
                  <p>
                    <strong>5.3 Third party reliance.</strong> Our advice is provided solely for your benefit and may not be relied upon by any third party without our express written consent.
                  </p>
                  <p>
                    <strong>5.4 No liability to third parties.</strong> We owe no duty of care to any person other than you. Nobody else may rely on our advice or services.
                  </p>
                  <p>
                    <strong>5.5 Exclusions.</strong> Nothing in these terms limits liability that cannot legally be limited, including liability for fraud or death/personal injury caused by negligence.
                  </p>
                </div>
              </section>

              {/* Section 6 */}
              <section id="advice" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">6. Our Legal Advice</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    <strong>6.1 Jurisdiction.</strong> Unless otherwise agreed in writing, we will only advise you on the laws of England and Wales, and to the extent applicable, EU law. For advice on other jurisdictions, we can introduce you to qualified counsel in those jurisdictions.
                  </p>
                  <p>
                    <strong>6.2 Information.</strong> You must provide us with complete, accurate, and timely information. Our advice is based on the information you provide. We are not liable for advice based on incomplete or inaccurate information.
                  </p>
                  <p>
                    <strong>6.3 Changes in law.</strong> We advise according to the law in force at the time we provide our advice. We will not update our advice for changes in law unless you specifically request us to do so in writing.
                  </p>
                  <p>
                    <strong>6.4 Scope changes.</strong> Any changes to the scope of our engagement must be agreed in writing.
                  </p>
                </div>
              </section>

              {/* Section 7 */}
              <section id="others" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">7. Work Done by Others</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    <strong>7.1 Third party professionals.</strong> Where we believe it is in your interests, we may recommend engaging third parties such as barristers, expert witnesses, or foreign counsel. The decision to engage them is yours, and you will be responsible for their fees.
                  </p>
                  <p>
                    <strong>7.2 Barristers.</strong> If your matter requires the services of a barrister, we will engage them on your behalf. You are responsible for their fees.
                  </p>
                  <p>
                    <strong>7.3 Reliance on others.</strong> We will rely on the work and advice of other professionals you engage, whether directly or through us.
                  </p>
                  <p>
                    <strong>7.4 No responsibility.</strong> We are not responsible for any errors, omissions, or deficiencies in the work of third parties you engage.
                  </p>
                </div>
              </section>

              {/* Section 8 */}
              <section id="information" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">8. Use of Information</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    <strong>8.1 Confidentiality.</strong> We will keep your affairs confidential, subject to our professional and legal obligations. We may share information within our firm and with service providers necessary to provide our services.
                  </p>
                  <p>
                    <strong>8.2 Disclosure.</strong> We may be required to disclose information to regulators, courts, tax authorities, or law enforcement. Where permitted, we will notify you of such disclosures.
                  </p>
                  <p>
                    <strong>8.3 Data protection.</strong> We process personal data in accordance with applicable data protection laws. Please see our <Link to="/privacy-policy" className="text-gray-900 underline">Privacy Policy</Link> for more information.
                  </p>
                  <p>
                    <strong>8.4 File retention.</strong> We retain client files for a minimum of seven years after the conclusion of a matter, or longer if required by law or professional rules.
                  </p>
                </div>
              </section>

              {/* Section 9 */}
              <section id="insurance" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">9. Insurance and Regulation</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    <strong>9.1 Professional indemnity insurance.</strong> We maintain professional indemnity insurance in accordance with regulatory requirements. Details of our insurance are available upon request.
                  </p>
                  <p>
                    <strong>9.2 Regulation.</strong> Ashbourne Law is regulated by the Solicitors Regulation Authority (SRA) in England and Wales. We comply with the SRA Standards and Regulations.
                  </p>
                  <p>
                    <strong>9.3 Financial services.</strong> We are not authorised by the Financial Conduct Authority. However, we may undertake certain regulated financial services activities as an exempt professional firm under the supervision of the SRA.
                  </p>
                </div>
              </section>

              {/* Section 10 */}
              <section id="identity" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">10. Proof of Identity and Anti-Money Laundering</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    <strong>10.1 Verification.</strong> Before we can act for you, we must verify your identity and, where applicable, the identity of beneficial owners. We may request original documents or certified copies.
                  </p>
                  <p>
                    <strong>10.2 Electronic checks.</strong> We may conduct electronic identity verification checks, which may leave a footprint on your credit file but will not affect your credit rating.
                  </p>
                  <p>
                    <strong>10.3 Source of funds.</strong> We may request information about the source of funds used in your matter.
                  </p>
                  <p>
                    <strong>10.4 Reporting obligations.</strong> We are legally obliged to report suspicious activity to the National Crime Agency. If we make such a report, we may be prohibited from informing you.
                  </p>
                  <p>
                    <strong>10.5 Sanctions.</strong> You must inform us if you or your matter may be subject to international sanctions. We cannot act if doing so would breach sanctions laws.
                  </p>
                </div>
              </section>

              {/* Section 11 */}
              <section id="complaints" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">11. Complaints</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    <strong>11.1 Our complaints procedure.</strong> If you are unhappy with our services, please contact us immediately. We have a formal complaints procedure and will investigate your concerns promptly and fairly.
                  </p>
                  <p>
                    <strong>11.2 Legal Ombudsman.</strong> If you are not satisfied with our handling of your complaint, you may be eligible to refer the matter to the Legal Ombudsman (<a href="https://www.legalombudsman.org.uk" className="text-gray-900 underline" target="_blank" rel="noopener noreferrer">www.legalombudsman.org.uk</a>).
                  </p>
                  <p>
                    <strong>11.3 Time limits.</strong> Complaints to the Legal Ombudsman must normally be made within six months of receiving our final response and within one year of the act or omission complained about.
                  </p>
                  <p>
                    <strong>11.4 Costs assessment.</strong> You have the right to apply to the court for an assessment of our costs under the Solicitors Act 1974, subject to strict time limits.
                  </p>
                </div>
              </section>

              {/* Section 12 */}
              <section id="communications" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">12. Communications</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    <strong>12.1 Email.</strong> Unless you instruct otherwise, we may communicate with you by email. By providing an email address, you consent to receiving invoices and other communications by email.
                  </p>
                  <p>
                    <strong>12.2 Security.</strong> Email is not a completely secure method of communication. We cannot guarantee the security of emails sent over the internet.
                  </p>
                  <p>
                    <strong>12.3 Authority.</strong> We will accept instructions from any person we reasonably believe is authorized to instruct us on your behalf. You must inform us of any limits on authority.
                  </p>
                  <p>
                    <strong>12.4 File sharing.</strong> We can provide secure methods of file sharing. Use of insecure file-sharing methods is at your own risk.
                  </p>
                </div>
              </section>

              {/* Section 13 */}
              <section id="termination" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">13. Termination and Suspension</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    <strong>13.1 Your right to terminate.</strong> You may terminate our engagement at any time by giving written notice. You will remain liable for fees incurred up to the date of termination.
                  </p>
                  <p>
                    <strong>13.2 Our right to terminate.</strong> We may terminate or suspend our services if you breach these terms, fail to pay when due, fail to provide adequate instructions, or if continuing would breach our professional obligations.
                  </p>
                  <p>
                    <strong>13.3 Effect of termination.</strong> Upon termination, we will cease work on your matter (except for work necessary to properly withdraw from the engagement). All outstanding fees and disbursements will become immediately due and payable.
                  </p>
                  <p>
                    <strong>13.4 Return of documents.</strong> Upon request and payment of all outstanding fees, we will return your original documents. We will retain copies for our records.
                  </p>
                </div>
              </section>

              {/* Section 14 */}
              <section id="law" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">14. Governing Law and Jurisdiction</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    <strong>14.1 English law.</strong> These terms and our engagement are governed by the laws of England and Wales.
                  </p>
                  <p>
                    <strong>14.2 Jurisdiction.</strong> The courts of England and Wales have exclusive jurisdiction to settle any dispute arising from our engagement.
                  </p>
                  <p>
                    <strong>14.3 Contracts (Rights of Third Parties) Act 1999.</strong> No person who is not a party to our engagement has any right under the Contracts (Rights of Third Parties) Act 1999 to enforce any term of our engagement.
                  </p>
                </div>
              </section>

              {/* Contact box */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mt-8">
                <div className="flex items-start space-x-4">
                  <FileText className="h-6 w-6 text-gray-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">Questions about our Terms?</h3>
                    <p className="text-gray-600 text-sm mb-4">
                      If you have any questions about these Terms of Engagement, please contact us.
                    </p>
                    <Link
                      to="/contact"
                      className="inline-flex items-center text-sm font-medium text-gray-900 hover:underline"
                    >
                      Contact us
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Link>
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-500 mt-8">
                Last updated: December 2024
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfEngagement;